import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import EnhancedTable from "../table/Table";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { getProjectStatusAdmin } from "../../redux/actions/ProjectStatusActions";
import { fetchAdminProjects } from "../../redux/actions/ProjectActions";
import { isValidArr } from "../../utility/utils";
import Loader from "../../components/loader/Loader";
import DialogBox from "../../components/dialog-box/DialogBox";
import ProjectStatusForm from "./ProjectStatusForm";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Tooltip } from "@mui/material";
import { GetProjectStatusModel } from "../../models/ProjectStatusModel";
import { fetchUsers } from "../../redux/actions/UserActions";

type iData = {
  id: number;
  projectName: string;
  buApproved: string;
  buApprover: string;
  sbuApproved: string;
  sbuApprover: string;
  sbgApproved: string;
  sbgApprover: string;
  csgoApproved: string;
  csgoApprover: string;
  rejected: string;
  rejectedUserName: string;
};

const ProjectStatusPage = () => {
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState<GetProjectStatusModel>();
  const [isBuApprovalRequired, setIsBuApprovalRequired] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();

  const columns = useMemo(() => {
    return [
      {
        id: "id",
        numeric: false,
        disablePadding: false,
        label: "Project ID",
        width: 180,
      },
      {
        id: "projectName",
        numeric: false,
        disablePadding: false,
        label: "Project Name",
        width: 250,
        renderCell: (params: GridRenderCellParams) => (
          <ExpandableCell {...params} />
        ),
      },
      {
        id: "buApproved",
        numeric: false,
        disablePadding: false,
        label: "BuApproved",
        width: 150,
      },
      {
        id: "buApprover",
        numeric: false,
        disablePadding: false,
        label: "BuApprover",
        width: 150,
      },
      {
        id: "sbuApproved",
        numeric: false,
        disablePadding: false,
        label: "SbuApproved",
        width: 150,
      },
      {
        id: "sbuApprover",
        numeric: false,
        disablePadding: false,
        label: "SbuApprover",
        width: 150,
      },
      {
        id: "sbgApproved",
        numeric: false,
        disablePadding: false,
        label: "SbgApproved",
        width: 150,
      },
      {
        id: "sbgApprover",
        numeric: false,
        disablePadding: false,
        label: "SbgApprover",
        width: 150,
      },
      {
        id: "csgoApproved",
        numeric: false,
        disablePadding: false,
        label: "CsgoApproved",
        width: 150,
      },
      {
        id: "csgoApprover",
        numeric: false,
        disablePadding: false,
        label: "CsgoApprover",
        width: 150,
      },
      {
        id: "rejected",
        numeric: false,
        disablePadding: false,
        label: "Rejected",
        width: 150,
      },
      {
        id: "rejectedUserName",
        numeric: false,
        disablePadding: false,
        label: "RejectedUserName",
        width: 150,
      },
    ];
  }, []);

  useEffect(() => {
    dispatch(getProjectStatusAdmin());
    dispatch(fetchAdminProjects());
    dispatch(fetchUsers());
  }, [dispatch]);

  const allProjects = useAppSelector((state) => state.project.allAdminProjects);

  const isAdminProjectsFetching = useAppSelector(
    (state) => state.project.isAdminProjectsFetching
  );
  const allProjectStatus = useAppSelector(
    (state) => state.projectStatus.getProjectStatusArrayObject
  );

  const allBus = useAppSelector((state) => state.bu.all_BUs);

  const allUsers = useAppSelector((state) => state.user.all_Users);

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info: any) =>
          Object.values(info).join("").toLowerCase().includes(searchVal)
        )
        : rootData
    );
  };

  useEffect(() => {
    setRootData(() =>
      allProjectStatus.map((status) => {
        const proj = allProjects?.find(
          (project) => project.projectId === status.projectId
        );
        const buUser = status.buapproverUserId
          ? allUsers?.find((user) => user.userId === status.buapproverUserId)
          : "";
        const buUserName = buUser
          ? `${buUser.firstName} ${buUser.lastName}`
          : "";
        const sbuUser = status.sbuapproverUserId
          ? allUsers?.find((user) => user.userId === status.sbuapproverUserId)
          : "";
        const sbuUserName = sbuUser
          ? `${sbuUser.firstName} ${sbuUser.lastName}`
          : "";

        const sbgUser = status.sbgapproverUserId
          ? allUsers?.find((user) => user.userId === status.sbgapproverUserId)
          : "";
        const sbgUserName = sbgUser
          ? `${sbgUser.firstName} ${sbgUser.lastName}`
          : "";

        const csgoUser = status.csgoapproverUserId
          ? allUsers?.find((user) => user.userId === status.csgoapproverUserId)
          : "";
        const csgoUserName = csgoUser
          ? `${csgoUser.firstName} ${csgoUser.lastName}`
          : "";

        const rejectedUser = status.rejectedUserId
          ? allUsers?.find((user) => user.userId === status.rejectedUserId)
          : "";

        const rejectedUsername = rejectedUser
          ? `${rejectedUser.firstName} ${rejectedUser.lastName}`
          : "";

        return {
          id: status.projectId,
          projectName: proj?.projectName || "",
          buApproved: status.isBuapproved ? "Yes" : "No",
          buApprover: buUserName,
          sbuApproved: status.isSbuapproved ? "Yes" : "No",
          sbuApprover: sbuUserName,
          sbgApproved: status.isSbgapproved ? "Yes" : "No",
          sbgApprover: sbgUserName,
          csgoApproved: status.isCsgoapproved ? "Yes" : "No",
          csgoApprover: csgoUserName,
          rejected: status.isRejected ? "Yes" : "No",
          rejectedUserName: rejectedUsername,
        };
      })
    );
  }, [allProjectStatus, allUsers, allProjects]);

  function ExpandableCell({ value }: GridRenderCellParams) {
    const [expanded, setExpanded] = useState(false);
    const sliceValue = 45;
    return (
      <Tooltip title={value} placement="top-start">
        <Box>
          <>
            {value.slice(0, sliceValue)} {<span>...</span>}
          </>

          {/* {expanded ? value : value.slice(0, sliceValue)}&nbsp;
          {value.length > sliceValue && (
            <Link
              type="button"
              component="button"
              sx={{ fontSize: "inherit" }}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "view less" : "view more"}
            </Link>
            
          )} */}
        </Box>
      </Tooltip>
    );
  }

  const handleEdit = (id: string | number) => {
    const editStatus = allProjectStatus.find((rec) => rec.projectId === id);
    const projBuId = allProjects.find((proj) => proj.projectId === id)?.buid;
    const bu = allBus.find((bu) => bu.buid === projBuId);
    setIsBuApprovalRequired(bu?.isApprovalRequired ? true : false);
    setEditData(editStatus);
    setIsModalOpen(true);
  };
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);
  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  return (
    <>
      {!isValidArr(allProjects) && isAdminProjectsFetching ? <Loader /> : null}
      <PageTitle title="Projects Status" />
      <PageSearch placeholder="Search Projects" searchHandler={searchHandler} />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={"Edit Status"}
          handleModalClose={handleModalClose}
        >
          <ProjectStatusForm
            data={editData}
            isBuApprovalRequired={isBuApprovalRequired}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default ProjectStatusPage;
